import { cn } from "@/lib/utils";

interface WaveLoaderProps {
  /** Number of dots (default: 5) */
  dots?: number;
  /** Size of each dot in pixels (default: 10) */
  dotSize?: number;
  /** Custom className for the container */
  className?: string;
  /** Custom className for each dot */
  dotClassName?: string;
  /** Title text (default: "Processing...") */
  title?: string;
  /** Subtitle text (default: "This may take a few seconds") */
  subtitle?: string;
  /** Show text below dots (default: true) */
  showText?: boolean;
}

export function WaveLoader({
  dots = 5,
  dotSize = 10,
  className,
  dotClassName,
  title = "Processing...",
  subtitle = "This may take a few seconds",
  showText = true,
}: WaveLoaderProps) {
  return (
    <div
      className={cn(
        "rounded-xl bg-muted/30 border border-border p-6 flex flex-col items-center gap-4",
        className
      )}
    >
      {/* Wave Dots Animation */}
      <div className="flex items-center gap-1.5">
        {Array.from({ length: dots }).map((_, i) => (
          <div
            key={i}
            className={cn(
              "rounded-full bg-purple-500 dark:bg-purple-400",
              dotClassName
            )}
            style={{
              width: dotSize,
              height: dotSize,
              animation: "wave 1.2s ease-in-out infinite",
              animationDelay: `${i * 0.1}s`,
            }}
          />
        ))}
      </div>
      {showText && (
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
        </div>
      )}
      <style>{`
        @keyframes wave {
          0%, 100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          50% {
            transform: translateY(-8px);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

/** Minimal wave dots without container - for inline use */
export function WaveDots({
  dots = 5,
  dotSize = 10,
  className,
  dotClassName,
}: Pick<WaveLoaderProps, "dots" | "dotSize" | "className" | "dotClassName">) {
  return (
    <>
      <div className={cn("flex items-center gap-1.5", className)}>
        {Array.from({ length: dots }).map((_, i) => (
          <div
            key={i}
            className={cn(
              "rounded-full bg-purple-500 dark:bg-purple-400",
              dotClassName
            )}
            style={{
              width: dotSize,
              height: dotSize,
              animation: "wave 1.2s ease-in-out infinite",
              animationDelay: `${i * 0.1}s`,
            }}
          />
        ))}
      </div>
      <style>{`
        @keyframes wave {
          0%, 100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          50% {
            transform: translateY(-8px);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}
